import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ArrowUpDown,
  Columns3,
  CreditCard,
  Download,
  FileText,
  Plus,
  Search,
  Settings,
  SlidersHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  FormSection,
  IconTile,
  SectionDivider,
  SettingRow,
  TabHeader,
  WipTag,
} from "./profile-form";
import { ProfileTable, Td, Th, TablePaginationBar } from "./profile-table";
import { TableBody, TableHeader, TableRow } from "@/components/ui/table";

const INVOICES = [
  { invoice: "INV-2026-002", period: "Feb 2026", amount: "$99.99", method: "Visa •••• 1234" },
  { invoice: "INV-2026-001", period: "Jan 2026", amount: "$99.99", method: "Visa •••• 1234" },
  { invoice: "INV-2025-012", period: "Dec 2025", amount: "$99.99", method: "Visa •••• 1234" },
  { invoice: "INV-2025-011", period: "Nov 2025", amount: "$99.99", method: "Visa •••• 1234" },
  { invoice: "INV-2025-010", period: "Oct 2025", amount: "$99.99", method: "Visa •••• 1234" },
];

export function BillingTab() {
  const { t } = useLingui();
  const [search, setSearch] = useState("");

  return (
    <div className="flex flex-col gap-8">
      <TabHeader
        title={t`Billing & Subscriptions`}
        description={t`Manage your plan, payment methods and invoices.`}
        tag={<WipTag />}
      />

      <FormSection
        title={t`Subscription`}
        description={t`Plan details and seat usage.`}
      >
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Subscription details</Trans>
            </p>
            <p className="text-sm text-(--color-text-secondary)">
              <Trans>An overview of your plan, usage, and next billing date.</Trans>
            </p>
          </div>

          <div className="flex flex-col gap-4 rounded-lg border border-(--color-border) bg-(--color-surface-muted) p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <IconTile>
                  <FileText className="size-5" />
                </IconTile>
                <div>
                  <p className="text-sm text-(--color-text-secondary)">
                    <Trans>Your plan</Trans>
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium text-(--color-text-primary)">
                      Yes, Chef Pro
                    </span>
                    <span className="inline-flex rounded-full bg-(--color-surface-accent) px-2.5 py-0.5 text-xs font-medium text-(--color-text-secondary)">
                      <Trans>Annual</Trans>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="brand-outline" size="sm">
                  <Settings />
                  <Trans>Manage</Trans>
                </Button>
                <Button size="sm">
                  <Trans>Update payment</Trans>
                </Button>
              </div>
            </div>

            <div className="h-px bg-(--color-border)" />

            <div className="grid grid-cols-1 divide-y divide-(--color-border) sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-4 py-1 first:pl-0">
                <p className="text-sm text-(--color-text-secondary)">
                  <Trans>Total amount</Trans>
                </p>
                <p className="text-base font-medium text-(--color-text-primary)">
                  $99.99
                </p>
              </div>
              <div className="px-4 py-1 first:pl-0">
                <p className="text-sm text-(--color-text-secondary)">
                  <Trans>Next invoice</Trans>
                </p>
                <p className="text-base font-medium text-(--color-text-primary)">
                  23 Feb, 2026
                </p>
              </div>
              <div className="px-4 py-1 first:pl-0">
                <p className="text-sm text-(--color-text-secondary)">
                  <Trans>Cycle</Trans>
                </p>
                <p className="text-base font-medium text-(--color-text-primary)">
                  <Trans>Annual</Trans>
                </p>
              </div>
            </div>
          </div>
        </div>
      </FormSection>

      <SectionDivider />

      <FormSection
        title={t`Payment method`}
        description={t`Default method for renewals and invoices.`}
      >
        <div className="flex flex-col gap-3">
          <SettingRow
            icon={<CreditCard className="size-5" />}
            title={
              <span className="flex items-center gap-2">
                <Trans>Visa ending in 1234</Trans>
                <span className="inline-flex rounded-full bg-(--color-success-bg) px-2.5 py-0.5 text-xs font-medium text-(--color-success-text)">
                  <Trans>Default</Trans>
                </span>
              </span>
            }
            description={t`Exp. date 02/25`}
            action={
              <Button variant="destructive-subtle" size="sm">
                <Trans>Remove</Trans>
              </Button>
            }
          />
          <SettingRow
            icon={<CreditCard className="size-5" />}
            title={t`Webmoney ending in 1234`}
            description={t`Exp. date 02/25`}
            action={
              <>
                <Button variant="brand-outline" size="sm">
                  <Trans>Set as default</Trans>
                </Button>
                <Button variant="destructive-subtle" size="sm">
                  <Trans>Remove</Trans>
                </Button>
              </>
            }
          />
          <SettingRow
            icon={<CreditCard className="size-5" />}
            title={t`Mastercard ending in 5678`}
            description={t`Exp. date 06/26`}
            action={
              <>
                <Button variant="brand-outline" size="sm">
                  <Trans>Set as default</Trans>
                </Button>
                <Button variant="destructive-subtle" size="sm">
                  <Trans>Remove</Trans>
                </Button>
              </>
            }
          />
          <SettingRow
            icon={<CreditCard className="size-5" />}
            title={t`Mastercard ending in 9012`}
            description={t`Exp. date 11/27`}
            action={
              <>
                <Button variant="brand-outline" size="sm">
                  <Trans>Set as default</Trans>
                </Button>
                <Button variant="destructive-subtle" size="sm">
                  <Trans>Remove</Trans>
                </Button>
              </>
            }
          />
          <Button variant="brand-outline" size="sm" className="w-fit">
            <Plus />
            <Trans>Add payment method</Trans>
          </Button>
        </div>
      </FormSection>

      <SectionDivider />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Billing history</Trans>
            </p>
            <p className="text-sm text-(--color-text-secondary)">
              <Trans>Download past invoices.</Trans>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t`Search…`}
                className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) pl-9 pr-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) sm:w-64"
              />
            </div>
            <Button variant="brand-outline">
              <Columns3 />
              <Trans>Columns</Trans>
            </Button>
            <Button variant="brand-outline">
              <ArrowUpDown />
              <Trans>Sort</Trans>
            </Button>
            <Button variant="brand-outline">
              <SlidersHorizontal />
              <Trans>Filter</Trans>
            </Button>
            <Button>
              <Download />
              <Trans>Download all</Trans>
            </Button>
          </div>
        </div>

        <ProfileTable>
          <TableHeader>
            <TableRow>
              <Th><Trans>Invoice</Trans></Th>
              <Th><Trans>Period</Trans></Th>
              <Th sorted><Trans>Amount</Trans></Th>
              <Th><Trans>Payment method</Trans></Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {INVOICES.map((row) => (
              <TableRow key={row.invoice}>
                <Td>{row.invoice}</Td>
                <Td>{row.period}</Td>
                <Td>{row.amount}</Td>
                <Td>{row.method}</Td>
              </TableRow>
            ))}
          </TableBody>
        </ProfileTable>

        <TablePaginationBar shown={5} total={5} />
      </div>
    </div>
  );
}
